import { useNavigate, useSearch } from '@tanstack/react-router';
import type { JSX } from 'react';
import { Page, PageHeader } from '@/components/layout/page.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs.js';
import { PARAMETRES_TABS, readParametresSearch, type ParametresTab } from './search-params.js';
import { AlertesTab } from './tab-alertes.js';
import { AvanceTab } from './tab-avance.js';
import { BoutiquesTab } from './tab-boutiques.js';
import { KlaviyoTab } from './tab-klaviyo.js';
import { MarteletTab } from './tab-martelet.js';
import { PennylaneTab } from './tab-pennylane.js';
import { RechargeTab } from './tab-recharge.js';

const TAB_LABELS: Record<ParametresTab, string> = {
  boutiques: 'Shopify',
  martelet: 'Martelet',
  pennylane: 'Pennylane',
  recharge: 'Recharge',
  klaviyo: 'Klaviyo',
  alertes: 'Alertes',
  avance: 'Avancé',
};

/**
 * Écran Paramètres — `02` §12, refondu le 10/09/2026 : onglets groupés par
 * domaine (une boutique, un partenaire, un canal d'alerte) plutôt que par
 * technologie ; Recharge s'ajoute après Pennylane (`06` §8.6), puis Klaviyo (`06` §11.4). Tout ce qui concerne Martelet — FTP, suivi, noms de
 * transporteur, suspension des envois — tient maintenant dans un seul onglet.
 *
 * L'onglet actif vit dans l'URL (`?tab=`) pour rester « bookmarkable » ; les
 * anciens noms d'onglet sont redirigés par `resolveParametresTab`.
 *
 * `Tabs` de Radix ne monte que le contenu de l'onglet actif (pas de
 * `forceMount`) : une seule requête réseau à la fois, sans coupure manuelle.
 *
 * Largeur `form` (880 px) pour **tous** les onglets : ce sont des réglages, et
 * la liste la plus large (boutiques, cinq colonnes courtes) y tient. Une seule
 * largeur évite que la barre d'onglets change de taille d'un onglet à l'autre.
 */
export function ParametresPage(): JSX.Element {
  const rawSearch: unknown = useSearch({ strict: false });
  const navigate = useNavigate();
  const { tab } = readParametresSearch(rawSearch);

  function setTab(next: ParametresTab): void {
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, tab: next, store: undefined }),
      replace: true,
    });
  }

  return (
    <Page width="form">
      <PageHeader
        title="Paramètres"
        description="Shopify, Pennylane, Recharge, Klaviyo, Martelet, alertes et réglages avancés."
      />

      <Tabs
        value={tab}
        onValueChange={(next) => {
          setTab(next as ParametresTab);
        }}
      >
        <TabsList>
          {PARAMETRES_TABS.map((code) => (
            <TabsTrigger key={code} value={code}>
              {TAB_LABELS[code]}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="boutiques">
          <BoutiquesTab />
        </TabsContent>
        <TabsContent value="martelet">
          <MarteletTab />
        </TabsContent>
        <TabsContent value="pennylane">
          <PennylaneTab />
        </TabsContent>
        <TabsContent value="recharge">
          <RechargeTab />
        </TabsContent>
        <TabsContent value="klaviyo">
          <KlaviyoTab />
        </TabsContent>
        <TabsContent value="alertes">
          <AlertesTab />
        </TabsContent>
        <TabsContent value="avance">
          <AvanceTab />
        </TabsContent>
      </Tabs>
    </Page>
  );
}
